"use client";

import { useState, useEffect, useRef, type ReactNode } from "react";
import Link from "next/link";
import { getPanchang } from "@/lib/api/panchang";
import type { PanchangResponse } from "@/lib/api/panchang";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { DEFAULT_PANCHANG_CITY, usePanchangCity } from "@/contexts/PanchangCity";
import type { CityData } from "@/lib/cities-data.types";
import PanchangDateNavigator from "./PanchangDateNavigator";
import PanchangDepthPanels from "./PanchangDepthPanels";
import PanchangHeader from "./PanchangHeader";
import PanchangNowStrip from "./PanchangNowStrip";
import TimingCityPicker from "./TimingCityPicker";
import {
  PanchangResultSkeleton,
  PanchangCalculationDisclosure,
  TithiNakshatraCard,
  SunMoonTimingsCard,
  QuickInfoGrid,
  YogaKaranaRow,
  InauspiciousPeriodsCard,
  PlanetPositionsDisclosure,
} from "./Panchang.sections";

function formatDate(date: Date, timeZone: string, language: "en" | "hi"): string {
  return date.toLocaleDateString(language === "hi" ? "hi-IN" : "en-IN", {
    timeZone,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function toDateString(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const y = values.year;
  const m = values.month;
  const d = values.day;
  return `${y}-${m}-${d}`;
}

export interface PanchangProps {
  language?: "en" | "hi";
  headingLevel?: "h1" | "h2";
  initialDate?: string;
  initialDisplayDate?: string;
  initialResult?: PanchangResponse | null;
  initialCity?: CityData;
  allowCityChange?: boolean;
  /** Required governed offer: rendered after core value and before optional result depth. */
  resultOffer: ReactNode;
  crossLinks: ReactNode;
}

function errorMessage(error: unknown, isHindi: boolean): string {
  if (error instanceof Error) return error.message;
  return isHindi ? "पंचांग लोड नहीं हो सका" : "Failed to load panchang";
}

function usePanchangResult({
  apiDate,
  city,
  initialResult,
  initialResultMatches,
  isHindi,
}: {
  apiDate: string;
  city: CityData;
  initialResult: PanchangResponse | null;
  initialResultMatches: boolean;
  isHindi: boolean;
}) {
  const [loading, setLoading] = useState(!initialResultMatches);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PanchangResponse | null>(initialResult);
  const trackedInitialResult = useRef(false);
  const canUseInitialResult = initialResultMatches && result === initialResult;

  useEffect(() => {
    if (canUseInitialResult) {
      if (!trackedInitialResult.current) {
        trackedInitialResult.current = true;
        trackCalculatorUsed({ calculator: "panchang" });
      }
      return;
    }

    let cancelled = false;
    async function fetchPanchang() {
      setLoading(true);
      setError(null);
      try {
        const response = await getPanchang({
          date: apiDate,
          latitude: city.latitude,
          longitude: city.longitude,
          tz: city.timezone,
        });
        if (cancelled) return;
        setResult(response);
        trackCalculatorUsed({ calculator: "panchang" });
      } catch (fetchError) {
        if (cancelled) return;
        setError(errorMessage(fetchError, isHindi));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void fetchPanchang();
    return () => {
      cancelled = true;
    };
  }, [apiDate, canUseInitialResult, city, isHindi]);

  return { error, loading, result };
}

function PanchangLocationPicker({
  city,
  isHindi,
  onCityChange,
}: {
  city: CityData;
  isHindi: boolean;
  onCityChange: (city: CityData) => void;
}) {
  return (
    <TimingCityPicker
      key={city.slug}
      id="panchang-city"
      city={city}
      label={isHindi ? "अपने शहर का पंचांग देखें" : "Show Panchang for your city"}
      placeholder={isHindi ? "शहर खोजें" : "Search for a city"}
      help={
        isHindi
          ? "समय आपके चुने हुए शहर के अक्षांश, देशांतर और समय क्षेत्र से निकाला जाता है।"
          : "Timings use the selected city's latitude, longitude, and local timezone."
      }
      onCityChange={onCityChange}
    />
  );
}

function PersonalKundaliLink({ isHindi }: { isHindi: boolean }) {
  return (
    <div className="text-center">
      <Link
        href={isHindi ? "/hi/free-kundali/" : "/free-kundali/"}
        className="btn-sacred inline-block font-semibold py-3 px-6 rounded-lg text-body"
      >
        {isHindi ? "अपनी व्यक्तिगत कुंडली बनाएं →" : "Get Your Personal Kundali →"}
      </Link>
    </div>
  );
}

function PanchangResult({
  city,
  error,
  isHindi,
  loading,
  nowStrip,
  result,
  resultOffer,
}: {
  city: CityData;
  error: string | null;
  isHindi: boolean;
  loading: boolean;
  nowStrip: ReactNode;
  result: PanchangResponse | null;
  resultOffer: ReactNode;
}) {
  return (
    <div className="min-h-[1120px]">
      {loading && <PanchangResultSkeleton />}
      {error && !loading && (
        <div className="card-sacred p-6 text-center border-error-tint bg-error/5">
          <p className="text-body text-error">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-3 text-body-small font-semibold text-intent-brand"
          >
            Try Again
          </button>
        </div>
      )}
      {result && !loading && (
        <div className="space-y-6">
          {nowStrip}
          <TithiNakshatraCard result={result} />
          <SunMoonTimingsCard result={result} />
          <QuickInfoGrid result={result} />
          {resultOffer}
          <PanchangCalculationDisclosure result={result} city={city} isHindi={isHindi} />
          <YogaKaranaRow result={result} />
          <InauspiciousPeriodsCard result={result} />
          <PlanetPositionsDisclosure result={result} />
          <PanchangDepthPanels result={result} isHindi={isHindi} />
          <PersonalKundaliLink isHindi={isHindi} />
        </div>
      )}
    </div>
  );
}

/** The strip's window props, tolerant of a response without the block. */
function nowStripRahuKaal(
  result: { rahu_kaal?: { start: string; end: string } } | null
): { start: string; end: string } | null {
  return result?.rahu_kaal ? { start: result.rahu_kaal.start, end: result.rahu_kaal.end } : null;
}

export function Panchang({
  language = "en",
  headingLevel = "h1",
  initialDate,
  initialDisplayDate,
  initialResult = null,
  initialCity = DEFAULT_PANCHANG_CITY,
  allowCityChange = true,
  resultOffer,
  crossLinks,
}: PanchangProps) {
  const isHindi = language === "hi";
  const { city, setCity } = usePanchangCity();
  const isInitialCity = city.slug === initialCity.slug;
  const now = new Date();
  const todayForCity = toDateString(now, city.timezone);
  const serverDate = isInitialCity && initialDate ? initialDate : todayForCity;
  // The selected date is client state and never enters the URL: a dated URL per
  // city would mint the near-duplicate cohort that got the domain re-scored
  // (rule 66 #8 / ADR-0074). A date is a selector.
  const [selectedDate, setSelectedDate] = useState(serverDate);
  const apiDate = selectedDate;
  const isServerDate = apiDate === serverDate;
  const todayFormatted =
    isServerDate && isInitialCity && initialDisplayDate
      ? initialDisplayDate
      : formatDate(new Date(`${apiDate}T12:00:00Z`), city.timezone, language);
  const initialResultMatches = isInitialCity && initialResult?.date === apiDate;
  const { error, loading, result } = usePanchangResult({
    apiDate,
    city,
    initialResult,
    initialResultMatches,
    isHindi,
  });

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <PanchangHeader
          isHindi={isHindi}
          headingLevel={headingLevel}
          todayFormatted={todayFormatted}
          locationName={city.name}
        />

        <PanchangDateNavigator
          value={apiDate}
          today={todayForCity}
          isHindi={isHindi}
          onChange={setSelectedDate}
        />

        {allowCityChange && (
          <PanchangLocationPicker city={city} isHindi={isHindi} onCityChange={setCity} />
        )}

        <PanchangResult
          city={city}
          error={error}
          isHindi={isHindi}
          loading={loading}
          nowStrip={
            // "Right now" is only an honest claim on TODAY's view for the
            // selected city; a navigated date renders no live verdict.
            apiDate === todayForCity ? (
              <PanchangNowStrip
                city={city}
                isHindi={isHindi}
                todayForCity={todayForCity}
                rahuKaal={nowStripRahuKaal(result)}
              />
            ) : null
          }
          result={result}
          resultOffer={resultOffer}
        />

        {crossLinks}
      </div>
    </div>
  );
}
